import { useCallback } from 'react';

import { getCognitoToken, getWebpackEnvVar } from '@trqk/utils';

import { useModal } from '..';
import { RefreshTokenSignIn } from '.';

interface UseTokenModalResult {
    getToken: GetToken;
    getTokenAsync: GetTokenAsync;
}

/**
 * A hook that returns a function called `getToken`, which uses the Cognito SDK to get the
 * current token and attempts to refresh it if necessary. This function is intended to be
 * called immediately before the token is used, so that it doesn't get stale. The first input
 * to `getToken` is a callback that is invoked with the token, if available. There is an
 * optional second callback input which is invoked when the token cannot be retrieved.
 *
 * If the token cannot be retrieved (has expired and can't be refreshed):
 * - if there is a second input for `handleTokenMissing`, call it
 * - if not, display a modal to allow the user to enter their password and re-authenticate
 * @returns {UseTokenModalResult}
 */
export const useTokenModal = (): UseTokenModalResult => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const modal = useModal();

    const getToken = useCallback(
        (handleToken: TokenCallback, handleTokenMissing?: () => void): void => {
            const doGetCognitoToken = async (): Promise<boolean> => {
                const { token } = await getCognitoToken({
                    clientId,
                    userPoolId,
                });

                if (token !== null) {
                    handleToken(token);
                } else if (handleTokenMissing) {
                    handleTokenMissing();
                } else {
                    modal.add({
                        Content: RefreshTokenSignIn,
                        handleClose: ({ output }: RemoveModalInput): void => {
                            if (typeof output === 'string') {
                                handleToken(output);
                            }
                        },
                    });
                }

                // This value isn't used anywhere; we just return a consistent type for linting and
                // typechecking purposes.
                return true;
            };

            doGetCognitoToken();
        },
        [modal],
    );

    const getTokenAsync = useCallback(
        (): Promise<string> =>
            new Promise((resolve, reject) => {
                getCognitoToken({
                    clientId,
                    userPoolId,
                })
                    .then(result => {
                        const { token } = result;

                        if (token === null) {
                            modal.add({
                                Content: RefreshTokenSignIn,
                                handleClose: ({
                                    output,
                                }: RemoveModalInput): void => {
                                    if (typeof output === 'string') {
                                        resolve(output);
                                    } else {
                                        reject(
                                            new Error(
                                                'Failed to re-authenticate user',
                                            ),
                                        );
                                    }
                                },
                            });
                        } else {
                            resolve(token);
                        }
                    })
                    .catch(e => {
                        const err = e as Error;

                        reject(err);
                    });
            }),
        [modal],
    );

    return { getToken, getTokenAsync };
};
